import { useEffect, useState } from 'react';
import { apiFetch } from './api';

// Shopify custom app: read-only store context for agents (server
// routes/shopify.ts). Admin connect/check/disconnect live at the bottom.

export interface Money {
  amount: string;
  currency: string;
}

export interface ShopOrder {
  id: string;
  name: string;
  createdAt: string;
  cancelled: boolean;
  financialStatus: string | null;
  fulfillmentStatus: string | null;
  total: Money | null;
  items: Array<{ title: string; quantity: number }>;
  tracking: Array<{ company: string | null; number: string | null; url: string | null }>;
  statusUrl: string | null;
  adminUrl: string;
  customerName?: string | null;
  customerEmail?: string | null;
}

export interface ShopCustomer {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  ordersCount: number;
  totalSpent: Money | null;
  createdAt: string | null;
  note: string | null;
  tags: string[];
  location: string | null;
  adminUrl: string;
  orders: ShopOrder[];
}

export interface ShopProduct {
  id: string;
  title: string;
  status: string | null;
  totalInventory: number | null;
  url: string | null;
  image: string | null;
  priceMin: Money | null;
  priceMax: Money | null;
  variants: Array<{ title: string; sku: string | null; price: string | null; inventory: number | null; available: boolean }>;
  adminUrl: string;
}

export interface CartLine {
  title: string;
  variantTitle?: string;
  quantity: number;
  price: number;
  url?: string;
  image?: string;
}

export interface VisitorShopContext {
  shop: { domain: string; name: string | null; currency: string | null };
  match: { basis: 'email-channel' | 'storefront-login' | 'visitor-email'; verified: boolean; value: string } | null;
  customer: ShopCustomer | null;
  cart: { itemCount: number; totalPrice: number; currency: string; items: CartLine[]; updatedAt: string | null } | null;
}

const base = (siteId: string) => `/api/sites/${siteId}/shopify`;

export const getVisitorShop = (siteId: string, visitorId: string, refresh = false) =>
  apiFetch<VisitorShopContext>(`${base(siteId)}/visitors/${visitorId}${refresh ? '?refresh=1' : ''}`);

export const searchShopProducts = (siteId: string, q: string) =>
  apiFetch<{ products: ShopProduct[] }>(`${base(siteId)}/products?q=${encodeURIComponent(q)}`).then((r) => r.products);

export const findShopOrder = (siteId: string, num: string) =>
  apiFetch<{ order: ShopOrder | null }>(`${base(siteId)}/orders/${encodeURIComponent(num.replace(/^#/, ''))}`).then((r) => r.order);

// Whether a site has a store: asked once per site per page load.
const statusCache = new Map<string, Promise<boolean>>();

export function useShopifyConnected(siteId: string | undefined): boolean {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    if (!siteId) return;
    let live = true;
    let p = statusCache.get(siteId);
    if (!p) {
      p = apiFetch<{ connected: boolean }>(`${base(siteId)}/status`)
        .then((r) => r.connected)
        .catch(() => {
          // Server down or a blip: don't remember "no store" for the rest of
          // the page's life; the next conversation opened asks again.
          statusCache.delete(siteId);
          return false;
        });
      statusCache.set(siteId, p);
    }
    void p.then((c) => live && setConnected(c));
    return () => {
      live = false;
    };
  }, [siteId]);
  return connected;
}

/** Forget the cached answer, e.g. after an admin connects or disconnects. */
export const resetShopifyStatus = (siteId: string) => statusCache.delete(siteId);

/** A Shopify Money amount ("12.50" + "USD") for display. */
export function fmtMoney(m: Money | null | undefined): string {
  if (!m) return '—';
  const n = Number(m.amount);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: m.currency }).format(n);
  } catch {
    return `${m.amount} ${m.currency}`;
  }
}

/** /cart.js prices are integers in the currency's cents, for every currency. */
export const fmtCents = (cents: number, currency: string) => fmtMoney({ amount: String(cents / 100), currency });

// ─── Admin ───

export interface ShopifyConnectionView {
  shopDomain: string;
  shopName: string | null;
  currency: string | null;
  authMode: 'token' | 'client_credentials';
  aiEnabled: boolean;
  aiAllowedActions: string[];
  hasStorefrontToken: boolean;
  status: 'active' | 'error';
  lastError: string | null;
  lastCheckedAt: string | null;
  adminUrl: string;
}

const adminBase = (siteId: string) => `/api/admin/sites/${siteId}/shopify`;

export const getShopifyConnection = (siteId: string) =>
  apiFetch<{ connection: ShopifyConnectionView | null; requiredScopes: string[] }>(adminBase(siteId));

export const connectShopify = (
  siteId: string,
  input: { shopDomain: string; accessToken: string } | { shopDomain: string; clientId: string; clientSecret: string },
) =>
  apiFetch<{ connection: ShopifyConnectionView }>(adminBase(siteId), { method: 'PUT', body: JSON.stringify(input) });

export const checkShopify = (siteId: string) =>
  apiFetch<{ result: { ok: boolean; detail?: string }; connection: ShopifyConnectionView }>(`${adminBase(siteId)}/check`, {
    method: 'POST',
  });

export const disconnectShopify = (siteId: string) => apiFetch<void>(adminBase(siteId), { method: 'DELETE' });

export const saveShopifyAi = (siteId: string, input: { enabled: boolean; storefrontToken?: string; allowedActions?: string[] }) =>
  apiFetch<{ connection: ShopifyConnectionView }>(`${adminBase(siteId)}/ai`, { method: 'PUT', body: JSON.stringify(input) });
